import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import type { LeaderboardGame } from "../utils/apiBase";
import { leaderboardFetchUrls } from "../utils/apiBase";

type Entry = {
  userId: string;
  displayName: string;
  picture?: string;
  game: LeaderboardGame;
  points: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  winPercent: number;
};

function formatLeaderboardPrimary(e: Entry): string {
  const isGuest = e.userId.startsWith("guest:");
  const base = (e.displayName && e.displayName.trim()) || "Guest";
  if (isGuest && !base.toLowerCase().includes("+ guest")) {
    return `${base} + guest`;
  }
  return base;
}

type Props = {
  open: boolean;
  onClose: () => void;
  highlightUserId?: string | null;
  initialGame: LeaderboardGame;
};

export const LeaderboardDialog = ({ open, onClose, highlightUserId, initialGame }: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [selectedGame, setSelectedGame] = useState<LeaderboardGame>(initialGame);

  useEffect(() => {
    if (!open) return;
    setSelectedGame(initialGame);
  }, [open, initialGame]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        let lastErr: unknown;
        let ok = false;
        for (const url of leaderboardFetchUrls(50, selectedGame)) {
          try {
            const res = await fetch(url);
            if (!res.ok) {
              lastErr = new Error(`HTTP ${res.status}`);
              continue;
            }
            const data = (await res.json()) as { entries?: Entry[] };
            if (!cancelled) setEntries(data.entries ?? []);
            ok = true;
            break;
          } catch (e) {
            lastErr = e;
          }
        }
        if (!ok && !cancelled) {
          throw lastErr instanceof Error ? lastErr : new Error("Failed to fetch");
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedGame]);

  const tabLabelByGame: Record<LeaderboardGame, string> = {
    addata: "Addata",
    literature: "Literature",
    rummy: "Rummy",
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Leaderboard</DialogTitle>
      <DialogContent>
        <Tabs
          value={selectedGame}
          onChange={(_e, value: LeaderboardGame) => setSelectedGame(value)}
          variant="fullWidth"
          sx={{ mb: 1.5 }}
        >
          <Tab value="addata" label={tabLabelByGame.addata} />
          <Tab value="literature" label={tabLabelByGame.literature} />
          <Tab value="rummy" label={tabLabelByGame.rummy} />
        </Tabs>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        {!loading && !error && entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No ranked scores yet.
          </Typography>
        )}
        {!loading && !error && entries.length > 0 && (
          <List dense>
            {entries.map((e, i) => (
              <ListItem
                key={e.userId}
                sx={{
                  bgcolor:
                    highlightUserId && e.userId === highlightUserId
                      ? "action.selected"
                      : undefined,
                }}
              >
                <ListItemAvatar>
                  <Typography variant="body2" sx={{ width: 24, textAlign: "center", fontWeight: 700 }}>
                    {i + 1}
                  </Typography>
                </ListItemAvatar>
                <ListItemText
                  primary={formatLeaderboardPrimary(e)}
                  secondary={`${e.gamesPlayed} games • ${e.gamesWon} wins`}
                />
                <Typography variant="body2" sx={{ minWidth: 126, textAlign: "right" }}>
                  <strong>{e.winPercent.toFixed(1)}%</strong> Win Rate
                  <br />
                  <span style={{ opacity: 0.9 }}>{Math.max(0, e.points)} pts</span>
                </Typography>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};
